import { useCallback, useRef, useState } from 'react';

import { Braces, Copy, Key, Plus, Search, Trash2, Upload, X } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { ConnectorsTab } from '@/components/data-connectors/ConnectorsTab';
import { selectClass } from '@/components/data-connectors/MappingEditor';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDataConnectors } from '@/hooks/useDataConnectors';
import {
  useBackfillSearchText,
  useBatchDeleteStock,
  useCreateStockApiToken,
  useDealerKitStockListings,
  useDealerKitStockSyncState,
  useDeleteAllStock,
  useRevokeStockApiToken,
  useStockApiTokens,
  useSyncDealerKitStock,
  useUploadStockCsv,
} from '@/hooks/useDealerKit';

import { StockSearchTab } from './stock-search/StockSearchTab';

import type { CsvStockRow, DealerKitStockListingRow } from '@/lib/api/dealer-kit.api';

const vehicleLabel = (row: DealerKitStockListingRow): string => {
  const vehicle = (row.data?.vehicle ?? {}) as Record<string, unknown>;
  const parts = [vehicle.manufacturer, vehicle.model, vehicle.derivative].filter(
    (p): p is string => typeof p === 'string' && p.length > 0,
  );
  return parts.join(' ') || '—';
};

const JsonDialog = ({
  row,
  open,
  onOpenChange,
}: {
  row: DealerKitStockListingRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-[640px]">
      <DialogHeader>
        <DialogTitle className="text-base">
          Stock listing &mdash; {row.registration || row.stock_listing_id}
        </DialogTitle>
      </DialogHeader>
      <pre className="bg-muted max-h-[460px] overflow-auto rounded-md p-3 font-mono text-xs">
        {JSON.stringify(row.data ?? {}, null, 2)}
      </pre>
    </DialogContent>
  </Dialog>
);

const TokensSection = () => {
  const { data: tokens, isLoading } = useStockApiTokens();
  const createToken = useCreateStockApiToken();
  const revokeToken = useRevokeStockApiToken();
  const [tokenName, setTokenName] = useState('');
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCreate = async () => {
    if (!tokenName.trim()) return;
    const result = await createToken.mutateAsync(tokenName.trim());
    setCreatedToken(result.token);
    setTokenName('');
  };

  const handleCopy = async () => {
    if (!createdToken) return;
    await navigator.clipboard.writeText(createdToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Key className="text-muted-foreground h-5 w-5" />
          <CardTitle className="text-sm">API Tokens</CardTitle>
        </div>
        <CardDescription>
          Bearer tokens for external access to the stock search API (voice agents, custom tools).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {createdToken && (
          <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 p-3 dark:border-green-900 dark:bg-green-950">
            <code className="flex-1 text-xs break-all">{createdToken}</code>
            <Button variant="ghost" size="sm" onClick={handleCopy}>
              <Copy className="h-4 w-4" />
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setCreatedToken(null)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        <div className="flex gap-2">
          <Input
            placeholder="Token name (e.g. voice-agent)"
            value={tokenName}
            onChange={(e) => setTokenName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            className="max-w-xs text-sm"
          />
          <Button
            size="sm"
            onClick={handleCreate}
            disabled={!tokenName.trim() || createToken.isPending}
          >
            <Plus className="mr-1 h-4 w-4" />
            Create
          </Button>
        </div>

        {isLoading ? (
          <p className="text-muted-foreground text-sm">Loading...</p>
        ) : !tokens?.length ? (
          <p className="text-muted-foreground text-sm">No tokens created yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Prefix</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Used</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-16" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {tokens.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="text-sm font-medium">{t.name}</TableCell>
                  <TableCell className="font-mono text-xs">{t.token_prefix}...</TableCell>
                  <TableCell>
                    <Badge variant={t.is_active ? 'default' : 'secondary'}>
                      {t.is_active ? 'Active' : 'Revoked'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {t.last_used_at ? new Date(t.last_used_at).toLocaleString('en-GB') : 'Never'}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {new Date(t.created_at).toLocaleString('en-GB')}
                  </TableCell>
                  <TableCell>
                    {t.is_active && (
                      <button
                        onClick={() => revokeToken.mutate(t.id)}
                        className="text-destructive hover:text-destructive/80 rounded p-1"
                        title="Revoke"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

const parseCsvFields = (line: string): string[] => {
  const fields: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        field += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      fields.push(field.trim());
      field = '';
    } else {
      field += ch;
    }
  }
  fields.push(field.trim());
  return fields;
};

const parseCsv = (text: string): CsvStockRow[] => {
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return [];
  const headers = parseCsvFields(lines[0]).map((h) => h.toLowerCase().replace(/\s+/g, '_'));
  return lines.slice(1).map((line) => {
    const values = parseCsvFields(line);
    const row: Record<string, string> = {};
    headers.forEach((h, i) => {
      if (values[i]) row[h] = values[i];
    });
    return row as CsvStockRow;
  });
};

const CsvUploadSection = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [parsedRows, setParsedRows] = useState<CsvStockRow[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const uploadCsv = useUploadStockCsv();
  const [uploadResult, setUploadResult] = useState<{
    imported: number;
    errors: string[];
  } | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setUploadResult(null);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      setParsedRows(parseCsv(text));
    };
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    if (!parsedRows.length) return;
    try {
      const result = await uploadCsv.mutateAsync(parsedRows);
      setUploadResult(result);
      setParsedRows([]);
      setFileName(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch {
      setUploadResult({ imported: 0, errors: ['Upload failed. Please try again.'] });
    }
  };

  const handleClear = () => {
    setParsedRows([]);
    setFileName(null);
    setUploadResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Upload className="text-muted-foreground h-5 w-5" />
          <CardTitle className="text-sm">Upload CSV</CardTitle>
        </div>
        <CardDescription>
          Upload stock via CSV file. Known columns: stock_listing_id, registration, vin,
          manufacturer, model, derivative, colour, year, mileage, price, fuel_type, body_type,
          transmission, status, doors, seats, condition, notes. Any extra column (e.g. lot) is
          stored as a custom field and becomes searchable (lot:5,6,8).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <Input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="max-w-xs text-sm"
          />
          {fileName && (
            <button
              onClick={handleClear}
              className="text-muted-foreground hover:text-foreground rounded p-1"
              title="Clear"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {parsedRows.length > 0 && (
          <div className="flex items-center gap-3">
            <Badge variant="secondary">{parsedRows.length} rows parsed</Badge>
            <Button size="sm" onClick={handleUpload} disabled={uploadCsv.isPending}>
              <Upload className="mr-1 h-4 w-4" />
              {uploadCsv.isPending ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        )}

        {uploadResult && (
          <div
            className={`rounded-md border p-3 text-sm ${
              uploadResult.errors.length > 0
                ? 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950'
                : 'border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950'
            }`}
          >
            <p>Imported: {uploadResult.imported}</p>
            {uploadResult.errors.length > 0 && (
              <ul className="text-destructive mt-1 list-inside list-disc text-xs">
                {uploadResult.errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const DealerKitStockPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('mpage')) || 1;
  const search = searchParams.get('msearch') || '';
  const source = searchParams.get('msource') || 'all';
  const tab =
    searchParams.get('tab') === 'connectors'
      ? 'connectors'
      : searchParams.get('tab') === 'manage'
        ? 'manage'
        : 'search';
  const [searchInput, setSearchInput] = useState(search);
  const [jsonRow, setJsonRow] = useState<DealerKitStockListingRow | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data, isLoading } = useDealerKitStockListings(page, 20, search || undefined, source);
  const { data: connectors } = useDataConnectors();
  const connectorName = (id: string | null) =>
    id ? (connectors?.find((c) => c.id === id)?.name ?? 'Connector') : 'DealerKit';
  const { data: syncState } = useDealerKitStockSyncState();
  const syncStock = useSyncDealerKitStock();
  const backfill = useBackfillSearchText();
  const batchDelete = useBatchDeleteStock();
  const deleteAll = useDeleteAllStock();

  const pageIds = data?.data.filter((row) => !row.connector_id).map((row) => row.id) ?? [];
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };

  const handleBatchDelete = () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected stock listing(s)?`)) return;
    batchDelete.mutate([...selectedIds], {
      onSuccess: () => setSelectedIds(new Set()),
    });
  };

  const handleDeleteAll = () => {
    const msg =
      'Delete ALL DealerKit stock listings? Connector cars are kept. This cannot be undone.';
    if (!window.confirm(msg)) return;
    deleteAll.mutate(undefined, {
      onSuccess: () => setSelectedIds(new Set()),
    });
  };

  const updateParam = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (!value) {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        if (key !== 'mpage') next.delete('mpage');
        return next;
      });
    },
    [setSearchParams],
  );

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    const timeout = setTimeout(() => updateParam('msearch', value), 300);
    return () => clearTimeout(timeout);
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-foreground text-2xl font-bold">Stock</h1>
          <p className="text-muted-foreground text-sm">
            Search every car in stock — or manage DealerKit and connector stock.
            {syncState?.last_synced_at && (
              <span className="ml-2">
                Last synced: {new Date(syncState.last_synced_at).toLocaleString('en-GB')}
                {syncState.last_count != null && ` (${syncState.last_count} listings)`}
              </span>
            )}
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => updateParam('tab', v === 'search' ? '' : v)}>
        <TabsList>
          <TabsTrigger value="search">Search</TabsTrigger>
          <TabsTrigger value="manage">Manage</TabsTrigger>
          <TabsTrigger value="connectors">Data Connectors</TabsTrigger>
        </TabsList>

        <TabsContent value="connectors" className="mt-4">
          <ConnectorsTab />
        </TabsContent>

        <TabsContent value="search" className="mt-4">
          <StockSearchTab />
        </TabsContent>

        <TabsContent value="manage" className="mt-4 space-y-6">
          <div className="flex items-center gap-2">
            <Button onClick={handleDeleteAll} disabled={deleteAll.isPending} variant="destructive">
              <Trash2 className="mr-1 h-4 w-4" />
              {deleteAll.isPending ? 'Deleting...' : 'Delete All DealerKit Stock'}
            </Button>
            <Button
              variant="outline"
              onClick={() => backfill.mutate()}
              disabled={backfill.isPending}
            >
              {backfill.isPending
                ? 'Backfilling...'
                : backfill.data
                  ? `Backfilled ${backfill.data.updated}`
                  : 'Backfill search index'}
            </Button>
            <Button
              onClick={() => syncStock.mutate()}
              disabled={syncStock.isPending || syncState?.running}
              variant="outline"
            >
              {syncState?.running
                ? 'Syncing...'
                : syncStock.isPending
                  ? 'Starting...'
                  : 'Sync Stock'}
            </Button>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <TokensSection />
            <CsvUploadSection />
          </div>

          <div className="flex items-center gap-4">
            <div className="relative max-w-sm">
              <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder="Search reg, VIN, id, or field:value (e.g. lot:5,6,8)"
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>
            <select
              aria-label="Source"
              className={selectClass}
              value={source}
              onChange={(e) =>
                updateParam('msource', e.target.value === 'all' ? '' : e.target.value)
              }
            >
              <option value="all">All sources</option>
              <option value="dealerkit">DealerKit</option>
              {connectors?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">{selectedIds.size} selected</span>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBatchDelete}
                  disabled={batchDelete.isPending}
                >
                  <Trash2 className="mr-1 h-4 w-4" />
                  {batchDelete.isPending ? 'Deleting...' : 'Delete Selected'}
                </Button>
              </div>
            )}
          </div>

          {isLoading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : !data?.data.length ? (
            <p className="text-muted-foreground">
              No stock found. Run a sync from Settings &rarr; DealerKit to seed the mirror.
            </p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={allPageSelected}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Select all on page"
                      />
                    </TableHead>
                    <TableHead>Registration</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Stock Listing ID</TableHead>
                    <TableHead>Synced</TableHead>
                    <TableHead className="w-16">Data</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        {/* Connector cars are managed by their connector, not deleted here. */}
                        <Checkbox
                          checked={selectedIds.has(row.id)}
                          disabled={!!row.connector_id}
                          onCheckedChange={() => toggleSelect(row.id)}
                          aria-label={`Select ${row.registration || row.stock_listing_id}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm font-medium">
                        {row.registration || (
                          <span className="text-muted-foreground/50">&mdash;</span>
                        )}
                      </TableCell>
                      <TableCell>{vehicleLabel(row)}</TableCell>
                      <TableCell>
                        <Badge variant={row.connector_id ? 'secondary' : 'outline'}>
                          {connectorName(row.connector_id)}
                        </Badge>
                        {row.status === 'unavailable' && (
                          <span className="text-muted-foreground ml-1 text-xs">
                            (no longer listed)
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground font-mono text-xs">
                        {row.stock_listing_id}
                      </TableCell>
                      <TableCell className="text-sm">
                        {new Date(row.synced_at).toLocaleString('en-GB')}
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => setJsonRow(row)}
                          className="text-muted-foreground hover:text-foreground rounded p-1"
                          title="View JSON"
                        >
                          <Braces className="h-4 w-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {data.totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-muted-foreground text-sm">
                    Page {data.page} of {data.totalPages} ({data.total} listings)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => updateParam('mpage', String(page - 1))}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= data.totalPages}
                      onClick={() => updateParam('mpage', String(page + 1))}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {jsonRow && (
        <JsonDialog
          key={jsonRow.id}
          row={jsonRow}
          open={!!jsonRow}
          onOpenChange={(open) => !open && setJsonRow(null)}
        />
      )}
    </div>
  );
};
